import { useToast } from '@chakra-ui/react'
import type { AxiosError } from 'axios'
import type {
  EditOrganizationUnit,
  OrganizationUnit,
  OrganizationUnitProperties,
} from 'BrokerTsLib/src/dtos'
import {
  useCreateOrganizationUnit,
  useGetOrganizationUnitById,
  useGetOrganizationUnitProperties,
} from 'BrokerTsLib/src/hooks/useOrganizationUnit'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { DashboardHeader } from '@/containers/dashboard/DashboardHeader'
import { SidebarProvider } from '@/context/SidebarContext'
import { PageWrapper } from '@/layouts/components/PageWrapper'
import { Sidebar } from '@/layouts/components/Sidebar'
import type { BreadcrumbItems } from 'BrokerTsLib/src/containers/Breadcrumbs'
import { Breadcrumbs } from 'BrokerTsLib/src/containers/Breadcrumbs'

import { OrganizationForm } from '../../containers/organization/OrganizationForm'

export const OrganizationCreateDependentPage = () => {
  const { organizationUnitId: organizationUnitIdParam } = useParams()
  const organizationUnitId = Number(organizationUnitIdParam)

  const navigate = useNavigate()
  const toast = useToast()

  const { data: superiorOrganizationData, isLoading } =
    useGetOrganizationUnitById(organizationUnitId)
  const superiorOrganization = superiorOrganizationData?.data

  const { data: superiorOrganizationPropertiesData } =
    useGetOrganizationUnitProperties(organizationUnitId)
  const superiorOrganizationProperties = superiorOrganizationPropertiesData?.data

  const [organization] = useState<OrganizationUnit>({
    superiorOrganizationUnitId: organizationUnitId,
  } as OrganizationUnit)

  const [properties] = useState({
    allowGcpDigitalSignature:
      superiorOrganizationProperties?.allowGcpDigitalSignature,
  } as OrganizationUnitProperties)

  const { mutateAsync: create } = useCreateOrganizationUnit()
  const onSubmit = (values: EditOrganizationUnit) =>
    create(values)
      .then((response) => {
        navigate('/organization/' + response.data.id)

        return response.data
      })
      .catch((e: AxiosError) => {
        toast({
          title: 'Chyba při vytváření organizace',
          description: `${e.response?.status}: ${e.response?.statusText}`,
          status: 'error',
        })

        return e
      })

  const breadcrumbs = useMemo(() => {
    const items: BreadcrumbItems = [
      { to: '/organization/list', label: 'Seznam organizací' },
    ]

    if (superiorOrganization?.superiorOrganizationUnitId)
      items.push({
        to: `/organization/${superiorOrganization?.superiorOrganizationUnitId}`,
        label:
          superiorOrganization?.superiorOrganizationUnitName ??
          'Nadřízená organizace',
      })

    items.push({
      to: `/organization/${organizationUnitId}`,
      label: superiorOrganization?.name ?? 'Organizace',
    })

    items.push({
      to: `/organization/${organizationUnitId}/users/create`,
      label: 'Vytvořit podřízenou organizaci',
      current: true,
    })

    return items
  }, [organization, superiorOrganization])

  return (
    <SidebarProvider>
      <PageWrapper
        header={
          <>
            <Breadcrumbs items={breadcrumbs} />
            <DashboardHeader
              sx={{ py: 16 }}
              title="Vytvořit podřízenou organizaci"
            />
          </>
        }
        sidebar={<Sidebar />}
        mainProps={{
          sx: {
            pt: { base: 0, md: 8 },
            gap: 6,
            display: 'grid',
          },
        }}
      >
        <OrganizationForm
          organization={organization}
          properties={properties}
          onSubmit={onSubmit}
          isCreate
        />
      </PageWrapper>
    </SidebarProvider>
  )
}
